import type { ReactNode } from 'react'

type Tone = 'neutral' | 'primary' | 'success' | 'warn' | 'danger' | 'info' | 'teal' | 'violet' | 'pink'
type Size = 'sm' | 'md'

interface BadgeProps {
  tone?: Tone
  size?: Size
  /** Show as outlined (border + transparent bg) instead of soft fill. */
  outlined?: boolean
  /** Glass pill — for chips sitting directly on Liquid Glass surfaces. */
  glass?: boolean
  children: ReactNode
}

const FILL: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300',
  primary: 'bg-primary/10 text-primary',
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  warn:    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  danger:  'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  info:    'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  teal:    'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  violet:  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  pink:    'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
}
const OUTLINE: Record<Tone, string> = {
  neutral: 'border border-outline-variant text-on-surface-variant',
  primary: 'border border-primary/40 text-primary',
  success: 'border border-emerald-400 text-emerald-700 dark:text-emerald-300',
  warn:    'border border-amber-400 text-amber-800 dark:text-amber-300',
  danger:  'border border-rose-400 text-rose-700 dark:text-rose-300',
  info:    'border border-sky-400 text-sky-700 dark:text-sky-300',
  teal:    'border border-teal-400 text-teal-800 dark:text-teal-300',
  violet:  'border border-violet-400 text-violet-700 dark:text-violet-300',
  pink:    'border border-pink-400 text-pink-700 dark:text-pink-300',
}
const SIZE: Record<Size, string> = {
  sm: 'px-2 py-0.5 text-[10px]',
  md: 'px-2.5 py-1 text-xs',
}

/**
 * Status / category pill. NEVER hand-roll <span class="bg-emerald-100 …"> per page. See DESIGN.md § 8c.
 *
 * Tone semantics:
 *   neutral  — no status implied
 *   primary  — entity highlight (kategorie pills)
 *   success  — OK / valid / aktiv
 *   warn     — needs attention soon
 *   danger   — needs immediate action / failed / expired
 *   info     — informational tag
 *   teal / violet / pink — category colours WITHOUT status meaning, e.g. telling two companies apart
 *                   (Zeiterfassung: CleanPrompt = teal, A.H. = violet, Umzug = pink)
 */
const GLASS_TEXT: Record<Tone, string> = {
  neutral: 'text-on-surface-variant',
  primary: 'text-primary',
  success: 'text-emerald-700 dark:text-emerald-300',
  warn:    'text-amber-800 dark:text-amber-300',
  danger:  'text-rose-700 dark:text-rose-300',
  info:    'text-sky-700 dark:text-sky-300',
  teal:    'text-teal-800 dark:text-teal-300',
  violet:  'text-violet-700 dark:text-violet-300',
  pink:    'text-pink-700 dark:text-pink-300',
}

export function Badge({ tone = 'neutral', size = 'sm', outlined, glass, children }: BadgeProps) {
  const cls = glass
    ? `liquid-glass-pill ${GLASS_TEXT[tone]}`
    : outlined ? OUTLINE[tone] : FILL[tone]
  return (
    // whitespace-nowrap: a pill that wraps to two lines stops reading as a pill
    // ("Betrag weicht ab" broke across lines in narrow table columns). A badge
    // is a single token — the column must size to it, never the reverse.
    <span className={`inline-flex items-center whitespace-nowrap rounded-full font-medium tracking-tight ${cls} ${SIZE[size]}`}>
      {children}
    </span>
  )
}
